import {html} from '@isceco/widget-library2/external/lit'
import {unsafeHTML} from '@isceco/widget-library2/external/lit/directives/unsafe-html.js'
import '@isceco/widget-library2/basic-elements/List/StaticList'
import StringColumn from '@isceco/widget-library2/basic-elements/List/column/StringColumn'
import {renderKontakteContent as renderKontakteResultPage} from './kontakteResultPage.js'
import {renderMindestloehneContent as renderMindestloehneResultPage} from './mindestloehneResultPage.js'
import {
  RESULT_PAGE,
  resolveTrefferName,
  sanitizeRichTextHtml
} from './utils.js'

const ACTION_I18N_BY_PAGE = {
  [RESULT_PAGE.MINDESTLOEHNE]: 'mindestlohnrechner.actions.mindestloehne',
  [RESULT_PAGE.GAV_KURZ]: 'mindestlohnrechner.actions.gavKurz',
  [RESULT_PAGE.KONTAKTE]: 'mindestlohnrechner.actions.kontakte'
}
const ACCORDION_AUSKUENFTE_I18N_KEY = 'mindestlohnrechner.ergebnis.accordion.auskuenfte'
const HINT_I18N_PREFIX = 'mindestlohnrechner.details.hints.'
const NO_CONTACT_AVAILABLE_I18N_KEY = 'mindestlohnrechner.details.contacts.noContactAvailable'
const SALARY_LIST_COLUMNS = [
  new StringColumn({ key: 'label', text: '', weight: 2 }),
  new StringColumn({ key: 'value', text: '', weight: 1, align: 'right' })
]

function getHintTitleKey(section) {
  return `${HINT_I18N_PREFIX}${section}.title`
}

function getHintTextKey(section) {
  return `${HINT_I18N_PREFIX}${section}.text`
}

function createHintItem(section) {
  return {
    id: section,
    titleKey: getHintTitleKey(section),
    textKey: getHintTextKey(section)
  }
}

function formatNoMatchPlzOrt(contact) {
  return [contact.plz, contact.ort].filter(Boolean).join(' ').trim()
}

function formatInputDate(value) {
  if (!value || typeof value !== 'string') {
    return '-'
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) {
    return value
  }
  const [, year, month, day] = match
  return `${day}.${month}.${year}`
}

function buildNoMatchContactLines(contact) {
  const lines = []
  const plzOrt = formatNoMatchPlzOrt(contact)

  if (contact.name) {
    lines.push(html`<strong>${contact.name}</strong>`)
  }
  if (contact.zusatzname) {
    lines.push(contact.zusatzname)
  }
  if (contact.strasse) {
    lines.push(contact.strasse)
  }
  if (contact.zusatz) {
    lines.push(contact.zusatz)
  }
  if (plzOrt) {
    lines.push(plzOrt)
  }
  if (contact.tel) {
    lines.push(`Tel: ${contact.tel}`)
  }
  if (contact.fax) {
    lines.push(`Fax: ${contact.fax}`)
  }
  if (contact.email) {
    lines.push(`E-Mail: ${contact.email}`)
  }
  if (contact.url) {
    lines.push(html`<a href="${contact.url}" target="_blank" rel="noopener noreferrer">Internet</a>`)
  }

  return lines
}

function renderNoMatchContactItem(contact) {
  const lines = buildNoMatchContactLines(contact)
  return html`
    <div class="no-match-contact-item">
      ${lines.map(line => html`<div>${line}</div>`)}
    </div>
  `
}

function renderNoMatchContactsContent(ctx) {
  const advisoryText = html`<p>${ctx.i18n.translate('mindestlohnrechner.ergebnis.keinTreffer.auskuenfte')}</p>`
  if (ctx.noMatchContacts.length === 0) {
    return advisoryText
  }

  return html`
    <div>
      ${advisoryText}
      <div class="no-match-contacts">
        ${ctx.noMatchContacts.map(contact => renderNoMatchContactItem(contact))}
      </div>
    </div>
  `
}

function renderNoMatchCard(ctx) {
  const key = 'NO_MATCH'
  const sectionId = `${key}-auskuenfte`
  const isExpanded = Boolean(ctx.expandedSections[sectionId])
  const icon = isExpanded ? '−' : '+'
  const accordionContent = isExpanded
    ? html`<div class="accordion-content auskuenfte-content">${renderNoMatchContactsContent(ctx)}</div>`
    : ''

  return html`
    <isceco-card>
      <div class="result-card-content">
        <label class="selection-row">
          <input
            type="radio"
            name="vertragSelection"
            .checked="${ctx.selectedTrefferKey === key}"
            @change="${() => ctx.selectTreffer(key)}"
          />
          <span class="selection-title">${ctx.i18n.translate('mindestlohnrechner.ergebnis.keinTreffer.title')}</span>
        </label>
        <div class="accordion-group">
          <div class="accordion-item">
            <button class="accordion-toggle" @click="${() => ctx.toggleSection(sectionId)}">
              ${icon} ${ctx.i18n.translate(ACCORDION_AUSKUENFTE_I18N_KEY)}
            </button>
            ${accordionContent}
          </div>
        </div>
      </div>
    </isceco-card>
  `
}

function renderTrefferAccordion(ctx, treffer, key) {
  const isNav = (treffer?.typ || '').toUpperCase() === 'NAV'
  const navCantonalContacts = renderContactList(ctx.noMatchContacts)
  const sections = [
    {
      id: `${key}-betrieblich`,
      title: ctx.i18n.translate('mindestlohnrechner.ergebnis.accordion.betrieblich'),
      content: treffer.geltungsbereichBetrieblich || '-'
    },
    {
      id: `${key}-persoenlich`,
      title: ctx.i18n.translate('mindestlohnrechner.ergebnis.accordion.persoenlich'),
      content: treffer.geltungsbereichPersoenlich || '-'
    },
    {
      id: `${key}-auskuenfte`,
      title: ctx.i18n.translate(ACCORDION_AUSKUENFTE_I18N_KEY),
      content: treffer.weitereAuskunfte || '',
      templateContent: isNav
        ? (navCantonalContacts || html`<p>${ctx.i18n.translate(NO_CONTACT_AVAILABLE_I18N_KEY)}</p>`)
        : null
    }
  ]
  return html`
    <div class="accordion-group">
      ${sections.map(section => {
        const isExpanded = Boolean(ctx.expandedSections[section.id])
        const icon = isExpanded ? '−' : '+'
        let contentTemplate = section.templateContent
        if (contentTemplate == null) {
          contentTemplate = section.content
            ? unsafeHTML(sanitizeRichTextHtml(section.content))
            : ''
        }
        const contentClass = section.id.endsWith('-auskuenfte')
          ? 'accordion-content auskuenfte-content'
          : 'accordion-content'
        const accordionContent = isExpanded
          ? html`<div class="${contentClass}">${contentTemplate}</div>`
          : ''

        return html`
          <div class="accordion-item">
            <button class="accordion-toggle" @click="${() => ctx.toggleSection(section.id)}">
              ${icon} ${section.title}
            </button>
            ${accordionContent}
          </div>
        `
      })}
    </div>
  `
}

function renderTrefferSelectionCard(ctx, treffer) {
  const key = ctx.getTrefferKey(treffer)
  const lang = getLanguage()
  const name = resolveTrefferName(treffer, lang)
  return html`
    <isceco-card>
      <div class="result-card-content">
        <label class="selection-row">
          <input
            type="radio"
            name="vertragSelection"
            .checked="${ctx.selectedTrefferKey === key}"
            @change="${() => ctx.selectTreffer(key)}"
          />
          <span class="selection-title">${name}</span>
        </label>
        ${renderTrefferAccordion(ctx, treffer, key)}
      </div>
    </isceco-card>
  `
}

function renderSelectedContractSummaryContent(ctx) {
  const selectedTreffer = ctx.getSelectedTreffer()
  const meta = ctx.selectedContractMeta
  if (!selectedTreffer && !meta) {
    return ''
  }
  const lang = getLanguage()
  const name = meta ? resolveTrefferName(meta, lang) : resolveTrefferName(selectedTreffer, lang)
  const validFrom = meta?.aveStart || selectedTreffer?.aveStart
  const validTo = meta?.aveEnd || selectedTreffer?.aveEnd
  const originalLink = meta?.originalLink || selectedTreffer?.originalLink || ''
  return html`
    <p class="selected-contract-value">
      ${name}
      ${originalLink
        ? html`(<a href="${originalLink}" target="_blank" rel="noopener noreferrer">${ctx.i18n.translate('mindestlohnrechner.details.originalLinkShortLabel')}</a>)`
        : ''}
    </p>
    <p>${ctx.i18n.translate('mindestlohnrechner.details.validityLabel')}: ${formatInputDate(validFrom)} - ${formatInputDate(validTo)}</p>
  `
}

function renderInputSummary(ctx, options = {}) {
  const includeSelectedContract = Boolean(options.includeSelectedContract)
  if (!ctx.lastSearchInput) {
    return ''
  }
  return html`
    <div class="input-summary-wrapper">
      <isceco-card>
        <div class="input-summary-card">
          <h3>${ctx.i18n.translate('mindestlohnrechner.ergebnis.ihreAngaben')}</h3>
          <p>${ctx.i18n.translate('mindestlohnrechner.einsatzort.title')}: ${ctx.lastSearchInput.einsatzortLabel || '-'}</p>
          <p>${ctx.i18n.translate('mindestlohnrechner.beginn.title')}: ${formatInputDate(ctx.lastSearchInput.beginn)}</p>
          <p>${ctx.i18n.translate('mindestlohnrechner.ende.title')}: ${formatInputDate(ctx.lastSearchInput.ende)}</p>
          <p>${ctx.i18n.translate('mindestlohnrechner.beruf.title')}: ${ctx.lastSearchInput.suchbegriff || '-'}</p>
          ${includeSelectedContract ? renderSelectedContractSummaryContent(ctx) : ''}
        </div>
      </isceco-card>
    </div>
  `
}

function renderActionButtons(ctx) {
  const hasContractSelection = Boolean(ctx.getSelectedTreffer())
  const hasNoMatchSelection = ctx.selectedTrefferKey === 'NO_MATCH'
  if (hasNoMatchSelection) {
    return html`
      <div class="action-buttons">
        <isceco-button
          variant="primary"
          text="${ctx.i18n.translate('mindestlohnrechner.actions.showResult.label')}"
          @click="${() => ctx.openResultPage(RESULT_PAGE.NO_MATCH)}"
        ></isceco-button>
      </div>
    `
  }

  return html`
    <div class="action-buttons">
      <isceco-button
        variant="primary"
        text="${ctx.i18n.translate('mindestlohnrechner.actions.mindestloehne.label')}"
        title="${ctx.i18n.translate('mindestlohnrechner.actions.mindestloehne.hint')}"
        ?disabled="${!hasContractSelection}"
        @click="${() => ctx.openResultPage(RESULT_PAGE.MINDESTLOEHNE)}"
      ></isceco-button>
      <isceco-button
        variant="primary"
        text="${ctx.i18n.translate('mindestlohnrechner.actions.gavKurz.label')}"
        title="${ctx.i18n.translate('mindestlohnrechner.actions.gavKurz.hint')}"
        ?disabled="${!hasContractSelection}"
        @click="${() => ctx.openResultPage(RESULT_PAGE.GAV_KURZ)}"
      ></isceco-button>
      <isceco-button
        variant="primary"
        text="${ctx.i18n.translate('mindestlohnrechner.actions.kontakte.label')}"
        title="${ctx.i18n.translate('mindestlohnrechner.actions.kontakte.hint')}"
        ?disabled="${!hasContractSelection}"
        @click="${() => ctx.openResultPage(RESULT_PAGE.KONTAKTE)}"
      ></isceco-button>
    </div>
  `
}

function renderResultDetailFooterButtons(ctx) {
  return html`
    <div class="action-buttons detail-footer-buttons">
      <isceco-button
        variant="primary"
        text="${ctx.i18n.translate('mindestlohnrechner.details.backToSelection')}"
        @click="${() => ctx.backToSelection()}"
      ></isceco-button>
      <isceco-button
        variant="primary"
        icon="print"
        text="${ctx.isPdfLoading ? '...' : ctx.i18n.translate('mindestlohnrechner.details.print')}"
        ?disabled="${!ctx.canExportPdf()}"
        @click="${() => ctx.handlePdfExport()}"
      ></isceco-button>
      ${ctx.activeResultPage === RESULT_PAGE.MINDESTLOEHNE
        ? html`
          <isceco-button
            variant="primary"
            text="${ctx.i18n.translate('mindestlohnrechner.details.exportExcel')}"
            ?disabled="${ctx.isLoadingCriteria || ctx.isLoadingResult || !ctx.isSalaryTableReady()}"
            @click="${() => ctx.handleExcelExport()}"
          ></isceco-button>
        `
        : ''}
    </div>
  `
}

function renderContactList(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return ''
  }
  return html`
    <div class="no-match-contacts">
      ${items.map(contact => renderNoMatchContactItem(contact))}
    </div>
  `
}

function renderHintsCard(ctx, statePrefix, items) {
  const sectionKey = `${statePrefix}:hints`
  const expanded = Boolean(ctx.expandedHintSections[sectionKey])
  return html`
    <isceco-card class="details-hints-card">
      <div class="details-hints">
        <button class="accordion-toggle details-hints-title" @click="${() => ctx.toggleHintSection(sectionKey)}">
          ${expanded ? '−' : '+'} ${ctx.i18n.translate('mindestlohnrechner.details.hintsTitle')}
        </button>
        ${expanded ? html`<div class="details-hints-body">${items}</div>` : ''}
      </div>
    </isceco-card>
  `
}

function renderHintAccordionsByItems(ctx, hintItems, statePrefix) {
  const withOriginalLink = (hintText, item) => {
    const originalLink = String(ctx.selectedContractMeta?.originalLink || '').trim()
    if (statePrefix !== 'gavKurz' || item.id !== 'originalfassung' || !originalLink) {
      return hintText
    }
    const safeHref = originalLink.replace(/"/g, '&quot;')
    if (/<strong>[\s\S]*?<\/strong>/i.test(hintText)) {
      return hintText.replace(
        /<strong>([\s\S]*?)<\/strong>/i,
        `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"><strong>$1</strong></a>`
      )
    }
    return `${hintText} <a href="${safeHref}" target="_blank" rel="noopener noreferrer">${ctx.i18n.translate('mindestlohnrechner.details.originalLinkShortLabel')}</a>`
  }

  return renderHintsCard(ctx, statePrefix, hintItems.map(item => {
        const stateKey = `${statePrefix}:${item.id}`
        const expanded = Boolean(ctx.expandedHintSections[stateKey])
        const hintText = withOriginalLink(sanitizeRichTextHtml(ctx.i18n.translate(item.textKey)), item)
        let content = ''
        if (expanded) {
          content = html`<div class="accordion-content" @click="${event => ctx.handleHintContentClick(event)}">${unsafeHTML(hintText)}</div>`
        }
        return html`
          <div class="accordion-item no-background">
            <button class="accordion-toggle" @click="${() => ctx.toggleHintSection(stateKey)}">
              ${expanded ? '−' : '+'} ${ctx.i18n.translate(item.titleKey)}
            </button>
            ${content}
          </div>
        `
      }))
}

function renderKontakteContent(ctx) {
  return renderKontakteResultPage(ctx, {
    renderContactList,
    renderHintAccordionsByItems,
    createHintItem,
    noContactAvailableI18nKey: NO_CONTACT_AVAILABLE_I18N_KEY
  })
}

function renderGavKurzContent(ctx) {
  const groups = ctx.getGavKurzSummaryGroups()
  const hintItems = [
    {
      id: 'originalfassung',
      titleKey: 'mindestlohnrechner.details.gavKurzHints.original.title',
      textKey: 'mindestlohnrechner.details.gavKurzHints.original.text'
    },
    createHintItem('personalverleih'),
    createHintItem('rechtlicherHinweis')
  ]

  return html`
    <div class="detail-content-section">
      <isceco-card>
        <div class="gav-kurz-card-content">
          <h3>${ctx.i18n.translate('mindestlohnrechner.details.title.gavKurz')}</h3>
          <p>${ctx.i18n.translate('mindestlohnrechner.details.gavKurzIntro')}</p>
          ${groups.length === 0
            ? html`<p>-</p>`
            : groups.map(group => html`
              <h3 class="gav-kurz-group-title">${ctx.i18n.translate(group.titleKey)}</h3>
              ${group.items.map(item => {
                const sectionId = `gavKurz:${group.id}:${item.id}`
                const expanded = Boolean(ctx.expandedSections[sectionId])
                const content = expanded
                  ? html`<div class="accordion-content">${unsafeHTML(sanitizeRichTextHtml(item.content || ''))}</div>`
                  : ''
                return html`
                  <div class="accordion-item no-background">
                    <button class="accordion-toggle" @click="${() => ctx.toggleSection(sectionId)}">
                      ${expanded ? '−' : '+'} ${ctx.i18n.translate(item.titleKey)}
                    </button>
                    ${content}
                  </div>
                `
              })}
            `)}
        </div>
      </isceco-card>
      ${renderHintAccordionsByItems(ctx, hintItems, 'gavKurz')}
    </div>
  `
}

function renderHintAccordions(ctx) {
  const sections = [
    'vollzugskostenbeitraege',
    'internationalerLohnvergleich',
    'wechselkurse',
    'anerkennung',
    'personalverleih',
    'rechtlicherHinweis'
  ]
  return renderHintsCard(ctx, 'mindestloehne', sections.map(section => {
        const stateKey = `mindestloehne:${section}`
        const expanded = Boolean(ctx.expandedHintSections[stateKey])
        const titleKey = getHintTitleKey(section)
        const hintText = sanitizeRichTextHtml(ctx.i18n.translate(getHintTextKey(section)))
        let content = ''
        if (expanded) {
          content = html`<div class="accordion-content" @click="${event => ctx.handleHintContentClick(event)}">${unsafeHTML(hintText)}</div>`
        }
        return html`
          <div class="accordion-item no-background">
            <button class="accordion-toggle" @click="${() => ctx.toggleHintSection(stateKey)}">
              ${expanded ? '−' : '+'} ${ctx.i18n.translate(titleKey)}
            </button>
            ${content}
          </div>
        `
      }))
}

function renderMindestloehneContent(ctx) {
  return renderMindestloehneResultPage(ctx, {
    renderHintAccordions,
    salaryListColumns: SALARY_LIST_COLUMNS
  })
}

function renderNoMatchDetailContent(ctx) {
  const hintItems = [
    {
      id: 'originalfassung',
      titleKey: 'mindestlohnrechner.details.noMatchHints.original.title',
      textKey: 'mindestlohnrechner.details.noMatchHints.original.text'
    },
    {
      id: 'personalverleih',
      titleKey: 'mindestlohnrechner.details.hints.personalverleih.title',
      textKey: 'mindestlohnrechner.details.hints.personalverleih.text'
    },
    {
      id: 'rechtlicherHinweis',
      titleKey: 'mindestlohnrechner.details.hints.rechtlicherHinweis.title',
      textKey: 'mindestlohnrechner.details.hints.rechtlicherHinweis.text'
    }
  ]

  return html`
    <div class="detail-content-section">
      <isceco-card>
        <div class="no-match-card-content">
          <h3>${ctx.i18n.translate('mindestlohnrechner.details.contacts.forYouResponsible')}</h3>
          <p>${ctx.i18n.translate('mindestlohnrechner.ergebnis.keinTreffer.auskuenfte')}</p>
          <p class="contacts-subsection-title">${ctx.i18n.translate('mindestlohnrechner.details.contacts.cantonalAuthorities')}</p>
          ${renderContactList(ctx.noMatchContacts) || html`<p>${ctx.i18n.translate('mindestlohnrechner.details.contacts.noContactAvailable')}</p>`}
        </div>
      </isceco-card>
      <isceco-card class="no-match-salary-card">
        <div class="no-match-salary-card-content">
          <h3>${ctx.i18n.translate('mindestlohnrechner.details.noMatch.salaryRangeTitle')}</h3>
          <p>${ctx.i18n.translate('mindestlohnrechner.details.noMatch.salaryRangeText')}</p>
          <p>
            <a
              href="${ctx.i18n.translate('footer.lohnspanne.link')}"
              target="_blank"
              rel="noopener noreferrer"
            >${ctx.i18n.translate('mindestlohnrechner.details.noMatch.salaryRangeLinkLabel')}</a>
          </p>
        </div>
      </isceco-card>

      ${renderHintAccordionsByItems(ctx, hintItems, 'noMatch')}
    </div>
  `
}

function renderActiveDetailContent(ctx) {
  if (ctx.activeResultPage === RESULT_PAGE.NO_MATCH) {
    return renderNoMatchDetailContent(ctx)
  }
  if (ctx.activeResultPage === RESULT_PAGE.GAV_KURZ) {
    return renderGavKurzContent(ctx)
  }
  if (ctx.activeResultPage === RESULT_PAGE.KONTAKTE) {
    return renderKontakteContent(ctx)
  }
  return renderMindestloehneContent(ctx)
}

export function renderResultNavigationBar(ctx) {
  const pages = ctx.activeResultPage === RESULT_PAGE.NO_MATCH
    ? []
    : [RESULT_PAGE.MINDESTLOEHNE, RESULT_PAGE.GAV_KURZ, RESULT_PAGE.KONTAKTE]
  return html`
    <div class="result-nav-buttons">
      <isceco-button
        variant="primary"
        text="${ctx.i18n.translate('mindestlohnrechner.details.back')}"
        @click="${() => ctx.backToSelection()}"
      ></isceco-button>
      ${pages.map(page => {
        const baseKey = ACTION_I18N_BY_PAGE[page]
        const isActive = page === ctx.activeResultPage
        return html`
          <isceco-button
            class="${isActive ? 'is-active' : ''}"
            variant="primary"
            text="${ctx.i18n.translate(`${baseKey}.label`)}"
            title="${ctx.i18n.translate(`${baseKey}.hint`)}"
            @click="${() => ctx.openResultPage(page)}"
          ></isceco-button>
        `
      })}
    </div>
  `
}

function renderResultDetailsPage(ctx) {
  const isNoMatchPage = ctx.activeResultPage === RESULT_PAGE.NO_MATCH
  return html`
    <div class="result-section">
      ${renderInputSummary(ctx, {includeSelectedContract: !isNoMatchPage})}
      ${renderActiveDetailContent(ctx)}
      ${renderResultDetailFooterButtons(ctx)}
    </div>
  `
}

export function renderResultSection(ctx) {
  if (ctx.activeResultPage) {
    return renderResultDetailsPage(ctx)
  }
  const leadText = ctx.i18n.translate('mindestlohnrechner.ergebnis.lead')
  const hasTreffer = ctx.resultTreffer && ctx.resultTreffer.length > 0
  return html`
    <div class="result-section result-section--separated">
      <p>${leadText}</p>
      ${renderInputSummary(ctx)}
      ${!hasTreffer ? html`<p class="no-results">${ctx.i18n.translate('mindestlohnrechner.ergebnis.keineTreffer')}</p>` : ''}
      <div class="result-cards">
        ${hasTreffer ? ctx.resultTreffer.map(treffer => renderTrefferSelectionCard(ctx, treffer)) : ''}
        ${renderNoMatchCard(ctx)}
      </div>
      ${renderActionButtons(ctx)}
    </div>
  `
}
